/**
 * Clinics Hook
 * Custom hook to fetch and manage clinic data with debounced search
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { clinicsGraphqlService } from '@/services/api/clinics/clinicsGraphqlService';

interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}

interface UseClinicsReturn {
  clinics: Clinic[];
  loading: boolean;
  error: string | null;
  searchClinics: (query: string) => void;
}

/**
 * Hook to fetch clinics with debounced search
 * @param initialLimit - Initial number of records to fetch (default: 10)
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 */
export const useClinics = (initialLimit: number = 10, debounceMs: number = 300): UseClinicsReturn => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [allClinics, setAllClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { hasuraToken } = useAuth();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch all clinics initially
  const fetchAllClinics = useCallback(async () => {
    if (!hasuraToken) return;

    setLoading(true);
    setError(null);

    try {
      const data = await clinicsGraphqlService.getAvailableClinics(hasuraToken);
      setAllClinics(data);
      // Show only initial limit
      setClinics(data.slice(0, initialLimit));
    } catch (err) {
      console.error('Failed to fetch clinics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch clinics');
      setClinics([]);
      setAllClinics([]);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, initialLimit]);

  // Fetch clinics on mount
  useEffect(() => {
    fetchAllClinics();
  }, [fetchAllClinics]);

  // Debounced search function
  const searchClinics = useCallback((query: string) => {
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      if (!query.trim()) {
        // No query - show initial limit
        setClinics(allClinics.slice(0, initialLimit));
      } else {
        // Filter clinics by search query
        const filtered = allClinics.filter(clinic =>
          clinic.clinic_name.toLowerCase().includes(query.toLowerCase())
        );
        setClinics(filtered);
      }
    }, debounceMs);
  }, [allClinics, initialLimit, debounceMs]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    clinics,
    loading,
    error,
    searchClinics
  };
};
